import React from 'react';
import { Dialog, Box, Stack, Typography, IconButton, Button, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import LoyaltyOutlinedIcon from '@mui/icons-material/LoyaltyOutlined';
import NotesIcon from '@mui/icons-material/Notes';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { isPast, format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { getDateFormat, getTimeFormat } from '@utils/dateHelper';
import { displayPhoneNumber } from '../../../utils/phoneUtils';
import StatusChip from './StatusChip';
import type { Reservation } from '@app-types/models/reservation';

interface DetailRowProps {
  icon?: React.ReactNode;
  label?: string;
  children?: React.ReactNode;
}

const DetailRow: React.FC<DetailRowProps> = ({ icon, label, children }) => (
  <Stack direction="row" spacing={1.25} sx={{ py: 1.1, borderBottom: '1px solid #F1ECE6', fontSize: 14 }}>
    <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>
    <Typography fontSize={13} color="text.secondary" sx={{ minWidth: 64 }}>{label}</Typography>
    <Typography fontSize={14} sx={{ flex: 1 }}>{children}</Typography>
  </Stack>
);

const EDITABLE_STATUSES = new Set(['confirmed', 'pending-for-approval']);

interface BookingDetailSheetProps {
  reservation: Reservation | null;
  onClose: () => void;
  onEdit: (r: Reservation) => void;
  onApprove: (r: Reservation) => void;
  onComplete: (r: Reservation) => void;
  onNoShow: (r: Reservation) => void;
  onCancel: (r: Reservation) => void;
  onMessage: (r: Reservation) => void;
}

// Focused booking detail modal — replaces the old 14-column table row.
const BookingDetailSheet: React.FC<BookingDetailSheetProps> = ({ reservation, onClose, onEdit, onApprove, onComplete, onNoShow, onCancel, onMessage }) => {
  const { t, i18n } = useTranslation();
  if (!reservation) return null;

  const r = reservation;
  const past = isPast(new Date(r.start));
  const plan = (r?.planId?.[i18n.language === 'zh' ? 'zh_name' : 'name'] as string) || r?.planId?.name || '';
  const account = r.userId?.displayName || r.userId?.email;
  const priceText = (() => {
    const orig = r.originalAmount;
    const disc = r.discountedAmount;
    const coupon = r.couponId?.value;
    if (coupon) return `$${orig} − $${coupon} = $${disc}`;
    return disc != null ? `$${disc}` : (orig != null ? `$${orig}` : '—');
  })();

  const act = (fn: (r: Reservation) => void) => () => { fn(r); onClose(); };

  return (
    <Dialog open={!!reservation} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      {/* header */}
      <Box sx={{ bgcolor: 'secondary.main', p: 2.25 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography fontSize={18} fontWeight={700}>{r.customerName}</Typography>
            <Typography fontSize={13} fontWeight={600} color="primary.dark">{plan}</Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <StatusChip status={r.status} />
            <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
          </Stack>
        </Stack>
      </Box>

      {/* body */}
      <Box sx={{ px: 2.25, py: 1 }}>
        <DetailRow icon={<AccessTimeIcon fontSize="small" />} label={t('reservationsV2.detail.when')}>
          {getDateFormat(r.start as unknown as Date)} · {getTimeFormat(r.start)}–{getTimeFormat(r.end)}
        </DetailRow>
        <DetailRow icon={<PhoneIcon fontSize="small" />} label={t('reservationsV2.detail.phone')}>
          {displayPhoneNumber((r.customerPhone || r.userId?.phone) as string, t('common.noPhone', 'No phone'))}
        </DetailRow>
        {account && (
          <DetailRow icon={<PersonOutlineIcon fontSize="small" />} label={t('reservationsV2.detail.account')}>{account}</DetailRow>
        )}
        <DetailRow icon={<PaymentsOutlinedIcon fontSize="small" />} label={t('reservationsV2.detail.price')}>{priceText}</DetailRow>
        {r.couponId?.value != null && (
          <DetailRow icon={<LoyaltyOutlinedIcon fontSize="small" />} label={t('reservationsV2.detail.coupon')}>${r.couponId.value}</DetailRow>
        )}
        {r.paymentType && (
          <DetailRow icon={<CreditCardOutlinedIcon fontSize="small" />} label={t('reservationsV2.detail.payment')}>{r.paymentType}</DetailRow>
        )}
        {r.deductCredit != null && r.deductCredit !== 0 && (
          <DetailRow icon={<CreditCardOutlinedIcon fontSize="small" />} label={t('reservationsV2.detail.credit')}>${r.deductCredit}</DetailRow>
        )}
        {r.remark && (
          <DetailRow icon={<NotesIcon fontSize="small" />} label={t('reservationsV2.detail.note')}>{r.remark}</DetailRow>
        )}
        <DetailRow icon={<SellOutlinedIcon fontSize="small" />} label={t('reservationsV2.detail.source')}>{r.createdType || '—'}</DetailRow>
        {r.createdAt && (
          <DetailRow icon={<EventNoteOutlinedIcon fontSize="small" />} label={t('reservationsV2.detail.created')}>
            {format(new Date(r.createdAt), 'yyyy-MM-dd HH:mm')}
          </DetailRow>
        )}
      </Box>

      <Divider />

      {/* actions — contextual to status/time */}
      <Stack direction="row" spacing={1} sx={{ p: 2, flexWrap: 'wrap' }}>
        {r.status === 'pending-for-approval' && (
          <Button variant="contained" sx={{ flex: 1, minWidth: 100 }} onClick={act(onApprove)}>{t('reservationsV2.approve')}</Button>
        )}
        {past && r.status === 'confirmed' && (
          <>
            <Button variant="contained" color="success" sx={{ flex: 1, minWidth: 100 }} onClick={act(onComplete)}>{t('reservationsV2.done')}</Button>
            <Button variant="outlined" color="warning" sx={{ flex: 1, minWidth: 100 }} onClick={act(onNoShow)}>{t('reservationsV2.noShow')}</Button>
          </>
        )}
        {EDITABLE_STATUSES.has(r.status) && (
          <Button variant="outlined" startIcon={<EditOutlinedIcon />} sx={{ flex: 1, minWidth: 100 }} onClick={act(onEdit)}>
            {t('reservationsV2.editBooking')}
          </Button>
        )}
        <Button variant="outlined" color="secondary" sx={{ flex: 1, minWidth: 100 }} onClick={() => { onMessage(r); onClose(); }}>{t('reservationsV2.message')}</Button>
        {!past && r.status !== 'cancelled' && (
          <Button variant="outlined" color="error" sx={{ flex: 1, minWidth: 100 }} onClick={act(onCancel)}>{t('reservationsV2.cancel')}</Button>
        )}
      </Stack>
    </Dialog>
  );
};

export default BookingDetailSheet;
